import { createRouter, createWebHistory } from 'vue-router';
import { useAuthStore } from '@/store/useAuthStore.ts';
import { defaultNotify } from '@/lib/notify';
import i18n from '@/plugins/i18n';
import type { AnalyticsClient } from '@satellite/composables/useAnalytics';

/**
 * Builds Circe's router.
 *
 * Takes the analytics client rather than reaching for it: router hooks live outside the
 * Vue injection tree, so the alternative is a module-level registry — which is exactly
 * what this app's `analytics/sessionReplay.ts` used to be.
 */
export function createCirceRouter(analytics: AnalyticsClient) {
  const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes: [
      {
        path: '/',
        name: 'home',
        redirect: () => {
          const authStore = useAuthStore();
          return authStore.user ? '/tasks' : '/login';
        }
      },
      {
        path: '/login',
        name: 'login',
        component: () => import('../views/LoginPage.vue'),
        meta: {
          requireAuth: false
        }
      },
      {
        path: '/auth/reset-password/:resetEmail/:resetToken',
        name: 'resetPassword',
        component: () => import('../views/LoginPage.vue'),
        props: true,
        meta: {
          requireAuth: false
        }
      },
      {
        path: '/auth/verify-and-reset-password/:resetEmail/:resetToken',
        name: 'verifyAndResetPassword',
        component: () => import('../views/LoginPage.vue'),
        props: true,
        meta: {
          requireAuth: false,
          verifyAccount: true
        }
      },
      {
        path: '/tasks',
        name: 'tasks',
        component: () => import('../views/TasksPage.vue'),
        meta: {
          requireAuth: true
        }
      }
    ]
  });

  router.beforeEach(async (to, from, next) => {
    const authStore = useAuthStore();
    const requireAuth = to.meta.requireAuth;

    const token = to.query?.token as string | undefined;
    if (token && token !== 'undefined') {
      try {
        await authStore.loginWithToken(token);
        return next({ name: 'tasks', replace: true });
      } catch (error) {
        console.error('Token login failed:', error);
        defaultNotify(i18n.global.t('auth.errors.sessionInvalidOrExpired'), 'error', {
          title: i18n.global.t('auth.errors.authenticationFailed')
        });
        return next({ name: 'login', replace: true });
      }
    }

    if (requireAuth && !authStore.user) {
      next('/login');
    } else {
      next();
    }
  });

  router.afterEach(destinationRoute => {
    try {
      // Call useAuthStore() inside the hook, not at module scope, to avoid a
      // Pinia ordering issue (Pinia is installed before the router in main.ts).
      analytics.maybeStartSessionRecording({
        path: destinationRoute.path,
        orgType: useAuthStore().user?.orgType ?? null
      });
    } catch (error) {
      console.error('Failed to evaluate the session replay gate', error);
    }
  });

  return router;
}
